import { VehicleSearchForm } from '@/components/VehicleSearchForm/VehicleSearchForm';
import { useAppConstantsContext } from '@/context/AppConstants';
import { useReportRedirect } from '@/reports/index';
import { Box, Divider, styled, Text } from '@/theme/index';

interface MyGarageEmptyProps {
  /** Require Bumper connect */
  additionalTabs?: {
    label: string;
    content: JSX.Element;
    customHandler?: () => void;
  }[];
}

const ImageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '157px',
  backgroundColor: theme.palette.grey[300],
  marginBottom: theme.spacing(1),
}));

const StyledImage = styled('img')`
  max-width: 152px;
  max-height: 109px;
  object-fit: contain;
`;

/**
 * Component representing an empty garage with options to add a vehicle or research a vehicle.
 *
 * @component
 */
export const MyGarageEmpty: React.FC<MyGarageEmptyProps> = ({
  additionalTabs,
}) => {
  const {
    images: { imgAddVehicle },
  } = useAppConstantsContext();
  const { redirectToReportUrl } = useReportRedirect();

  function onSuccess({ permalink }: { permalink: string }) {
    redirectToReportUrl('vehicle', permalink);
  }

  return (
    <Box data-testid="my-garage-empty">
      <Divider sx={{ marginY: 2.5 }} />
      <Text variant="h3">Add a vehicle to My Garage</Text>
      <Text variant="body1">
        Keep up with estimated values, routine maintenance, warranties, recalls
        and more! Get alerts and recommendations on how to keep your vehicles in
        top shape and save money.
      </Text>
      <Box
        sx={{
          border: 1,
          borderColor: 'grey.300',
          borderRadius: 1,
          marginTop: 2.5,
        }}
      >
        <ImageContainer>
          <StyledImage src={imgAddVehicle} alt="Add vehicle" />
        </ImageContainer>
        <Box sx={{ p: 2.5 }}>
          <VehicleSearchForm
            claimReport
            onSuccess={onSuccess}
            searchButtonText="Add Vehicle"
            clearFields
            searchButtonSize="172px"
            additionalTabs={additionalTabs}
          />
        </Box>
      </Box>
      <Divider sx={{ marginY: 2.5 }} />
      <Text variant="h3">Research a vehicle</Text>
      <Box sx={{ p: 2.5, border: 1, borderRadius: 1, borderColor: 'divider' }}>
        <VehicleSearchForm
          onSuccess={onSuccess}
          searchButtonText="Search"
          clearFields
        />
      </Box>
      <Divider sx={{ marginY: 1.5 }} />
    </Box>
  );
};
